import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Star } from 'lucide-react';

import { listStudentFeedback } from '../../../services/StudentFeedbackService';
import { useWaypoint } from '../common/waypoint/WaypointProvider.jsx';
import useScrollLoadMore from '../common/useScrollLoadMore';
import '../common/waypoint/waypoint.css';
import '../common/lms-pages.css';
import '../Trainer/TrainerDashboard.css';
import './feedback-list.css';

const PAGE_SIZE = 10;

/* Which reviews to show. The rating bounds are the review sections' own
   (bad 1-3, good 4-5), sent to the list endpoint as maxRating / minRating --
   the server filters, the popup only asks. */
const BANDS = [
  { key: 'bad', label: 'Bad (1–3)', params: { maxRating: 3 } },
  { key: 'good', label: 'Good (4–5)', params: { minRating: 4 } },
  { key: 'all', label: 'All', params: {} },
];

const ratingPill = (n) => {
  const r = Number(n) || 0;
  if (r >= 4) return 'pgm-pill--good';
  if (r <= 3 && r > 0) return 'pgm-pill--bad';
  return '';
};

const formatWhen = (value, format) => (value ? moment(value).format(format) : '—');

/* Five stars and the number -- the feedback page's own (FeedbackList's
   RatingStars), drawn with the same fbl-stars classes. */
const RatingStars = ({ value, label }) => {
  const n = Number(value) || 0;
  return (
    <span className="fbl-stars" role="img" aria-label={`${label}: ${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={13} className={i <= n ? 'fbl-star is-on' : 'fbl-star'} aria-hidden="true" />
      ))}
      <b>{n || '—'}</b>
    </span>
  );
};

/**
 * One review, in full: the same facts, ratings, topic-by-topic ratings,
 * comment and reply as the feedback page's own review popup
 * (FeedbackList's FeedbackDetail), laid out as a card so a page of them
 * reads down the list.
 *
 * Everything is on the row the list returned. The server has decided what
 * this reader may see: an admin's copy names the student, a trainer's
 * arrives anonymous -- nothing is hidden here, and nothing could reveal it.
 */
const ReviewCard = ({ row, onOpen }) => {
  const topicRows = Array.isArray(row.topicBreakdown) ? row.topicBreakdown : [];
  const topicCount = Number(row.sessionTopicCount) || 0;
  const ratedCount = Number(row.topicsRatedCount) || 0;
  const hasStudent = !row.anonymous && (row.studentName || row.studentEmail);

  return (
    <li className={`rvl-card rvl-card--${Number(row.rating) >= 4 ? 'good' : 'bad'}`}>
      <header className="rvl-card-head">
        <div className="rvl-card-id">
          {row.batchId ? (
            <button type="button" className="wp-link rvl-card-title" onClick={() => onOpen('batch', row.batchId)}>
              {row.batchName || 'Unknown batch'}
            </button>
          ) : (
            <span className="rvl-card-title">{row.batchName || 'Unknown batch'}</span>
          )}
          <span className="rvl-card-sub">
            {formatWhen(row.date, 'dddd, D MMM YYYY')}
            {row.trainerName && (
              <>
                {' · Taught by '}
                {row.trainerId ? (
                  <button type="button" className="wp-link" onClick={() => onOpen('trainer', row.trainerId)}>
                    {row.trainerName}
                  </button>
                ) : (
                  row.trainerName
                )}
              </>
            )}
          </span>
        </div>
        <span className={`pgm-pill ${ratingPill(row.rating)} rvl-card-score`}>{row.rating} / 5</span>
      </header>

      <dl className="fbl-dl">
        {hasStudent ? (
          <div className="fbl-dl-item">
            <dt>Student</dt>
            <dd>
              {row.studentName || '—'}
              {row.studentEmail && <small>{row.studentEmail}</small>}
            </dd>
          </div>
        ) : row.anonymous ? (
          <div className="fbl-dl-item">
            <dt>Student</dt>
            <dd>Anonymous</dd>
          </div>
        ) : null}
        <div className="fbl-dl-item">
          <dt>Topics in the session</dt>
          <dd>{topicCount}</dd>
        </div>
        <div className="fbl-dl-item">
          <dt>Submitted</dt>
          <dd>{formatWhen(row.createdAt, 'D MMM YYYY, h:mm A')}</dd>
        </div>
        <div className="fbl-dl-item">
          <dt>Status</dt>
          <dd>{row.status || 'Submitted'}</dd>
        </div>
      </dl>

      <div className="fbl-ratings">
        <div className="fbl-fact">
          <span className="fbl-fact-label">Session</span>
          <RatingStars value={row.rating} label="Session" />
        </div>
        <div className="fbl-fact">
          <span className="fbl-fact-label">Trainer</span>
          {row.trainerRating ? (
            <RatingStars value={row.trainerRating} label="Trainer" />
          ) : (
            <span className="fbl-muted">Not rated</span>
          )}
        </div>
      </div>

      <h3 className="fbl-section">
        Topics covered <span className="fbl-count">{topicCount}</span>
      </h3>
      {topicCount === 0 && topicRows.length === 0 ? (
        <p className="fbl-muted">No topics were recorded for this session.</p>
      ) : (
        <>
          {topicRows.length > 0 && (
            <p className="fbl-topics-sum">
              {ratedCount} of {topicRows.length} rated individually
            </p>
          )}
          <ul className="fbl-topics">
            {topicRows.map((t, i) => (
              <li key={`${t.topic}-${i}`}>
                <div className="fbl-topic-row">
                  <span className="fbl-topic-name">
                    {t.topic}
                    {t.onRecord === false && <span className="fbl-tag fbl-tag--soft">Not on the session record</span>}
                  </span>
                  {Number(t.rating) > 0 ? (
                    <RatingStars value={t.rating} label={t.topic} />
                  ) : (
                    <span className="fbl-muted">Not rated</span>
                  )}
                </div>
                {t.comment && <p className="fbl-topic-note">{t.comment}</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      <h3 className="fbl-section">Comment</h3>
      {row.comment ? <p className="fbl-prose">{row.comment}</p> : <p className="fbl-muted">No comment written.</p>}

      {row.trainerNote && (
        <>
          <h3 className="fbl-section">
            Reply{row.reviewedByRole ? ` · ${row.reviewedByRole}` : ''}
            {row.reviewedAt ? ` · ${formatWhen(row.reviewedAt, 'D MMM YYYY')}` : ''}
          </h3>
          <p className="fbl-prose">{row.trainerNote}</p>
        </>
      )}
    </li>
  );
};

/**
 * Every review for one trainer, or one batch -- opened from the Reviews
 * button on the review sections and their popup.
 *
 * `filter` is { trainerId } or { batchId } (or both, for one batch + trainer
 * pair); `baseParams` are the page's own filters, so the dates and topic
 * chosen above still apply. Searched and paged by the SERVER
 * (GET /studentfeedbacks): search matches the comment and the topics, and
 * the server decides what this reader may see -- a trainer's copy arrives
 * with no student on it.
 *
 * It opens on top of whatever opened it; `rvl-modal` is how that popup's
 * Escape handler knows to leave this one to close first.
 */
const ReviewListPopup = ({ title, sub, filter, baseParams, initialBand = 'all', onClose }) => {
  const { open: openEntity } = useWaypoint();
  const [band, setBand] = useState(initialBand);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const seq = useRef(0);
  const listEndRef = useRef(null);

  // Search asks the server once typing pauses, and always from page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [query]);

  const paramsKey = JSON.stringify({ ...(baseParams || {}), ...(filter || {}) });

  /* A page at a time from the server, appended as the list is scrolled
     (useScrollLoadMore below); a new search or band starts from page 1. */
  useEffect(() => {
    const mine = ++seq.current;
    const append = page > 1;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setFailed(false);
    const bandParams = (BANDS.find((b) => b.key === band) || BANDS[2]).params;
    // The popup's own rating band replaces any single-rating filter chosen
    // on the page: it is the question this popup was opened to answer.
    const { rating, ...rest } = JSON.parse(paramsKey);
    listStudentFeedback({
      ...rest,
      ...bandParams,
      ...(search ? { search } : {}),
      page,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        if (mine !== seq.current) return;
        setTotal(Number(res.pagination?.total) || 0);
        setRows((prev) => {
          if (!append) return res.data;
          const seen = new Set(prev.map((r) => String(r.id)));
          return [...prev, ...res.data.filter((r) => !seen.has(String(r.id)))];
        });
      })
      .catch(() => {
        if (mine !== seq.current) return;
        if (!append) {
          setRows([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (mine !== seq.current) return;
        setLoading(false);
        setLoadingMore(false);
      });
  }, [paramsKey, band, search, page]);

  const hasMore = rows.length < total;
  // The sentinel is in the popup's scrolling body; the hook is rooted at the
  // viewport, which that body's scroll clips, so it works in here unchanged.
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loading || loadingMore || failed || !hasMore) return;
      setPage((p) => p + 1);
    },
    [rows.length, total, loading, loadingMore, failed],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // A trainer or batch opened from the rows below sits on top.
      if (document.querySelector('.wp-modal')) return;
      e.stopImmediatePropagation();
      onClose();
    };
    // Capture, so this -- the top popup -- hears Escape before the one under it.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);


  return (
    <div
      className="pg-modal rvl-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rvl-title"
      onClick={onClose}
    >
      <div className="pg-modal-panel pg-modal-panel--xl" onClick={(e) => e.stopPropagation()}>
        <header className="pgm-head">
          <div className="pgm-head-main">
            <span className="pg-modal-eyebrow">Feedbacks</span>
            <h2 className="pgm-batch" id="rvl-title">{title}</h2>
            {sub && <p className="pgm-sub">{sub}</p>}
          </div>
          <button type="button" className="pg-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          <div className="rvl-tools">
            <input
              type="search"
              className="pg-input pgm-search rvl-search"
              placeholder="Search comments and topics..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search feedbacks"
            />
            <div className="br-seg" role="group" aria-label="Which feedbacks">
              {BANDS.map((b) => (
                <button
                  type="button"
                  key={b.key}
                  aria-pressed={band === b.key}
                  className={`br-seg-btn${band === b.key ? ' is-on' : ''}`}
                  onClick={() => {
                    setBand(b.key);
                    setPage(1);
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
            <span className="rvl-total">
              {total} {total === 1 ? 'feedback' : 'feedbacks'}
            </span>
          </div>

          {loading && rows.length === 0 ? (
            <div className="att-picklist-foot">
              <span className="pg-spinner att-spinner" aria-hidden="true" />
              <span>Loading feedbacks...</span>
            </div>
          ) : failed ? (
            <p className="pgm-empty">That did not load. Try again in a moment.</p>
          ) : rows.length === 0 ? (
            <p className="pgm-empty">
              {search ? `No feedback matches “${search}”.` : 'No feedbacks for this selection.'}
            </p>
          ) : (
            <ol className="rvl-cards">
              {rows.map((r) => (
                <ReviewCard key={r.id} row={r} onOpen={openEntity} />
              ))}
            </ol>
          )}

          {/* Where the reader is, and the sentinel the observer watches. */}
          <div className="pg-scroll-foot" ref={listEndRef}>
            {hasMore ? (
              <>
                <span className="pg-scroll-count">
                  {rows.length} of {total}
                </span>
                {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
              </>
            ) : total > 0 ? (
              <span className="pg-scroll-count">
                All {total} {total === 1 ? 'feedback' : 'feedbacks'} loaded
              </span>
            ) : null}
          </div>
        </div>

        <footer className="pg-modal-foot">
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ReviewListPopup;
